// src/components/canvas/plan/PlanItemRow.tsx
//
// One goal on a Month, Season or Year list (spec 2026-10-14, "The pages").
// Tapping a month or season goal (a task) opens its panel, which holds every
// verb; a year goal opens in place with its few verbs (Done, Let go, Edit
// wording).
//
// Paper-style horizons (Scott, 2026-11-04): the row is the item, written in
// this period's own words. No verbs on the row, no "For …" line, and no tag
// for what stands under it one horizon down.
//
// A pending change card marks the row dashed (planPreview.ts): teal when it
// would change here, grey when it would leave, struck when done or gone.
//
// An open line orders by drag (calm pass, 2026-10-18): the desk drags it
// natively; touch holds it, through `liProps` (PlanOrderList).

import type { DragEvent, HTMLAttributes, Ref } from 'react'
import { useArrived } from '@/contexts/CanvasActivityContext'
import type { PlanNode } from '@/components/plan/constellation/model'
import type { PlanMark } from '@/components/plan/constellation/planPreview'
import { InlineComposer } from './InlineComposer'
import { ReadinessIcons } from '@/components/canvas/prep/ReadinessIcons'
import type { Readiness } from '@/lib/prep/readiness'

export interface RowVerb {
  label: string
  onSelect: () => void
}

export interface PlanItemRowProps {
  node: PlanNode
  done: boolean
  /** Moved on to a later period ("Moved to November"): kept as the record,
   *  quiet, with no verbs of its own here. */
  movedTo?: string
  /** The item a conversation just saved (focus param names it). */
  saved: boolean
  /** Tapping the title: opens the task's panel, or a year goal in place. */
  onOpen: () => void
  /** A year goal opened in place (only rows with `more` open in place). */
  expanded?: boolean
  /** The verbs a year goal shows when open. */
  more?: RowVerb[]
  editing?: boolean
  onSaveEdit?: (text: string) => Promise<boolean>
  onCancelEdit?: () => void
  meta?: string | null
  /** What the item carries, and what its plan above carries (tasks only). */
  readiness?: Readiness
  /** A pending change card's mark on this row. */
  preview?: PlanMark
  disabled?: boolean
  onDragStart?: (e: DragEvent) => void
  onDragEnd?: () => void
  /** The list item's own attributes: a held press's ref and listeners on
   *  touch, the order id the gap reads, and the gap and lifted marks. */
  liProps?: HTMLAttributes<HTMLLIElement> & { ref?: Ref<HTMLLIElement>; 'data-order-id'?: string }
  /** It orders by drag: a grip shows on hover. */
  orderable?: boolean
}

export function PlanItemRow(p: PlanItemRowProps) {
  const arrived = useArrived(p.node.id)
  const { node } = p
  const opensInPlace = !!p.more
  const state = [arrived ? 'is-arrived' : '', p.saved ? 'is-selected' : '', p.done ? 'is-done' : '', p.movedTo && !p.done ? 'is-moved' : ''].filter(Boolean).join(' ')
  const preview = p.preview ? ` is-preview is-${p.preview.tone}${p.preview.struck ? ' is-struck' : ''}` : ''
  const open = opensInPlace && !!p.expanded && !p.editing
  const { className: liClass, ...li } = p.liProps ?? {}
  return <li {...li} className={`plan-item${open ? ' is-open' : ''}${preview}${liClass ? ` ${liClass}` : ''}`} data-plan-key={node.key}
    draggable={!!p.onDragStart && !p.editing} onDragStart={p.onDragStart} onDragEnd={p.onDragEnd}>
    <div className={`canvas-item ${state}`}>
      {p.orderable && !p.editing && <span className="plan-grip" aria-hidden="true">⋮⋮</span>}
      {p.editing && p.onSaveEdit && p.onCancelEdit
        ? <InlineComposer label={`Wording for ${node.title}`} initial={node.title} onSave={p.onSaveEdit} onCancel={p.onCancelEdit} />
        : <button type="button" className="canvas-item-title plan-item-title" aria-expanded={opensInPlace ? !!p.expanded : undefined}
            onClick={p.onOpen}>
            {node.title}
          </button>}
      {p.readiness && !p.editing && <ReadinessIcons readiness={p.readiness} />}
      {p.saved && <span className="canvas-tag">Saved</span>}
      {p.done && !p.preview && <span className="canvas-tag plan-tag-done">{node.goal ? 'Met' : 'Done'}</span>}
      {p.movedTo && !p.done && !p.preview && <span className="canvas-tag plan-tag-moved">{p.movedTo}</span>}
    </div>
    {p.preview && <span className="wc-proposed-note plan-preview-note">{p.preview.note}</span>}
    {open && <div className="plan-item-more">
      {p.meta && <p className="plan-item-meta">{p.meta}</p>}
      {p.more!.map((m) => <button key={m.label} type="button" className="canvas-link" disabled={p.disabled} onClick={m.onSelect}>{m.label}</button>)}
    </div>}
  </li>
}
